import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, Container, Paper } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import './BrandPulse.css';
import './App.css';
import { AppProvider, useApp } from './contexts/AppContext';
import Layout from './components/Layout/Layout';
import DashboardLayout from './components/Layout/DashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';
import NotificationContainer from './components/NotificationContainer';
import { Box, Dialog } from '@mui/material';

// Landing Page Components
import Header from './components/LandingPage/Header';
import Home from './components/LandingPage/Home';
import Features from './components/LandingPage/Features';
import CTA from './components/LandingPage/CTA';
import Pricing from './components/LandingPage/Pricing';
import AboutUs from './components/LandingPage/AboutUs';
import Footer from './components/LandingPage/Footer';

// ONLY the brand audit wizard – all other audit / report pages are COMMENTED
import BrandAuditWizard from './components/BrandPulse/BrandAuditWizard';
import PublicAuditView from './components/BrandPulse/PublicAuditView';
// ─────────────────────────────────────────────────────────────────────────
// OLD / ADDITIONAL COMPONENTS – all commented out
// ─────────────────────────────────────────────────────────────────────────
// import AuditFormNew from './components/BrandPulse/AuditFormNew';
// import LoadingScreen from './components/BrandPulse/LoadingScreen';

// // Brand Pages
// import DomainAuthorityPage from './components/BrandPulse/DomainAuthorityPage';
// import PageSpeedPage from './components/BrandPulse/PageSpeedPage';
// import GoogleTrendsPage from './components/BrandPulse/GoogleTrendsPage';
// import SocialMediaPage from './components/BrandPulse/SocialMediaPage';
// import GmbAuditPage from './components/BrandPulse/GmbAuditPage';
// import TechnicalSEOPage from './components/BrandPulse/TechnicalSEOPage';
// import IndexingPage from './components/BrandPulse/IndexingPage';
// import BrandPresencePage from './components/BrandPulse/BrandPresencePage';

// // Competitor Pages
// import CompetitorDomainPage from './components/BrandPulse/CompetitorDomainPage';
// import CompetitorSpeedPage from './components/BrandPulse/CompetitorSpeedPage';
// import CompetitorTrendsPage from './components/BrandPulse/CompetitorTrendsPage';
// import CompetitorTechSEOPage from './components/BrandPulse/CompetitorTechSEOPage';
// import PaidAdsPage from './components/BrandPulse/PaidAdsPage';
// import AIVisibilityPage from './components/BrandPulse/AIVisibilityPage';

// // Research Pages
// import KeywordResearchPage from './components/BrandPulse/KeywordResearchPage';
// import PeopleAlsoAsk from './components/BrandPulse/PeopleAlsoAsk';
// import AIRecommendations from './components/BrandPulse/AIRecommendations';

// // Legacy SEO Components
// import SEOAnalyzer from './components/SEO/SEOAnalyzer';
// import KeywordResearch from './components/SEO/KeywordResearch';
// import MetaTagGenerator from './components/SEO/MetaTagGenerator';
// import Reports from './components/SEO/Reports';
// import AuditDashboard from './components/SEO/AuditDashboard';
// import CompleteAuditDashboard from './components/SEO/CompleteAuditDashboard';

// // Social Components (legacy)
// import ContentSuggestions from './components/Social/ContentSuggestions';
// import SocialPublisher from './components/Social/SocialPublisher';
// import SocialAnalytics from './components/Social/Analytics';
// import ContentGenerator from './components/Social/ContentGenerator';
// import AdCopyGenerator from './components/Social/AdCopyGenerator';
// import CompleteVideoContent from './components/Social/CompleteVideoContent';
// import VideoSnippetGenerator from './components/Social/VideoSnippetGenerator';
// import VideoContentSuggestions from './components/Social/VideoContentSuggestions';
// import VideoGenerator from './components/Social/VideoGenerator';
// import AnalysisForm from './components/ContentExpansion/AnalysisForm';
// import ResultsDisplay from './components/ContentExpansion/ResultsDisplay';
// import BacklinkAnalysis from './components/backlinkAnalysis/AnalysisForm';
// import AnalysisHistory from './components/backlinkAnalysis/AnalysisHistory';
// import BacklinkProgram from './components/BacklinkProgram/BacklinkProgram';

// // Content & Social Boost (legacy)
// import ContentCalendar from './components/ContentSocialBoost/ContentCalendar';
// import HashtagGenerator from './components/ContentSocialBoost/HashtagGenerator';
// import ImageSuggestions from './components/ContentSocialBoost/ImageSuggestions';
// import VoiceToBlog from './components/ContentSocialBoost/VoiceToBlog';

// // Smart Sales Enablers (legacy)
// import SEOChatbotWidget from './components/SEOChatbot/SEOChatbotWidget';
// import AdCampaignGenerator from './components/SEOChatbot/AdCampaignGenerator';
// import AdCampaignManager from './components/SEOChatbot/AdCampaignManager';
// import SocialMediaManager from './components/SEOChatbot/SocialMediaManager';

// // Analytics & Conversion (legacy)
// import PerformanceDashboard from './components/AnalyticsAndConversion/PerformanceDashboard';
// import LandingPageBuilder from './components/AnalyticsAndConversion/LandingPageBuilder';

// // SEO Plan Generator & Conversion (legacy)
// import SEOPlanDashboard from './components/Generator/SeoPlannerDashboard';

// // Google (legacy)
// import GoogleAnalyticsConnect from './components/google/GoogleAnalyticsConnect';
// import OAuthClientManager from './components/google/OAuthClientManager';

// // Pages (legacy)
// import Dashboard from './pages/Dashboard';
// import Projects from './pages/Projects';
// import ProjectDashboard from './pages/ProjectDashboard';
// import AdminProjectMapping from './pages/AdminProjectMapping';

import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';   // keep if needed later, but can also comment


// Theme (unchanged)
const theme = createTheme({ /* your existing theme */ });

// ─────────────────────────────────────────────────────────────────
// SINGLE LOGIN PAGE – no registration option
// ─────────────────────────────────────────────────────────────────
function LoginPage() {
  const { isAuthenticated } = useApp();

  // Already logged in? Go to app
  if (isAuthenticated) {
    return <Navigate to="/app/brand-audit" replace />;
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Login />
      </Paper>
    </Container>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────────
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppProvider>
        <Router>
          <NotificationContainer />
          <Routes>
            {/* Only login page at root – no registration */}
            <Route path="/" element={<LoginPage />} />

            {/* Public routes (no registration allowed) */}
            <Route path="/about" element={<AboutUs />} />
            <Route path="/audit/view/:auditId" element={<PublicAuditView />} />

            {/* Protected app area */}
            <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route path="brand-audit" element={<BrandAuditWizard />} />
              <Route index element={<Navigate to="brand-audit" replace />} />
            </Route>

            {/* Catch-all – redirect to login */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AppProvider>
    </ThemeProvider>
  );
}

export default App;

// import React, { useState } from 'react';
// import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
// import { ThemeProvider, createTheme } from '@mui/material/styles';
// import CssBaseline from '@mui/material/CssBaseline';
// import './App.css';
// import './BrandPulse.css';
// import { AppProvider, useApp } from './contexts/AppContext';
// import Layout from './components/Layout/Layout';
// import DashboardLayout from './components/Layout/DashboardLayout';
// import ProtectedRoute from './components/ProtectedRoute';
// import NotificationContainer from './components/NotificationContainer';
// import { Box, Dialog } from '@mui/material';

// // Landing Page Components
// import Header from './components/LandingPage/Header';
// import Home from './components/LandingPage/Home';
// import Features from './components/LandingPage/Features';
// import CTA from './components/LandingPage/CTA';
// import Pricing from './components/LandingPage/Pricing';
// import AboutUs from './components/LandingPage/AboutUs';
// import Footer from './components/LandingPage/Footer';


// import BrandAuditWizard from './components/BrandPulse/BrandAuditWizard';

// // New Audit Flow
// import AuditFormNew from './components/BrandPulse/AuditFormNew';
// import LoadingScreen from './components/BrandPulse/LoadingScreen';

// // Brand Pages
// import DomainAuthorityPage from './components/BrandPulse/DomainAuthorityPage';
// import PageSpeedPage from './components/BrandPulse/PageSpeedPage';
// import GoogleTrendsPage from './components/BrandPulse/GoogleTrendsPage';
// import SocialMediaPage from './components/BrandPulse/SocialMediaPage';
// import GmbAuditPage from './components/BrandPulse/GmbAuditPage';
// import TechnicalSEOPage from './components/BrandPulse/TechnicalSEOPage';
// import IndexingPage from './components/BrandPulse/IndexingPage';
// import BrandPresencePage from './components/BrandPulse/BrandPresencePage';

// // Competitor Pages
// import CompetitorDomainPage from './components/BrandPulse/CompetitorDomainPage';
// import CompetitorSpeedPage from './components/BrandPulse/CompetitorSpeedPage';
// import CompetitorTrendsPage from './components/BrandPulse/CompetitorTrendsPage'
// import CompetitorTechSEOPage from './components/BrandPulse/CompetitorTechSEOPage';
// import PaidAdsPage from './components/BrandPulse/PaidAdsPage';
// import AIVisibilityPage from './components/BrandPulse/AIVisibilityPage';

// // Research Pages
//  import KeywordResearchPage from './components/BrandPulse/KeywordResearchPage';
//  import PeopleAlsoAsk from './components/BrandPulse/PeopleAlsoAsk';
// import AIRecommendations from './components/BrandPulse/AIRecommendations';

// // Legacy SEO Components (keep for backward compatibility)
// import SEOAnalyzer from './components/SEO/SEOAnalyzer';
// import KeywordResearch from './components/SEO/KeywordResearch';
// import MetaTagGenerator from './components/SEO/MetaTagGenerator';
// import Reports from './components/SEO/Reports';
// import AuditDashboard from './components/SEO/AuditDashboard';
// import CompleteAuditDashboard from './components/SEO/CompleteAuditDashboard';

// // Social Components (legacy)
// import ContentSuggestions from './components/Social/ContentSuggestions';
// import SocialPublisher from './components/Social/SocialPublisher';
// import SocialAnalytics from './components/Social/Analytics';
// import ContentGenerator from './components/Social/ContentGenerator';
// import AdCopyGenerator from './components/Social/AdCopyGenerator';
// import CompleteVideoContent from './components/Social/CompleteVideoContent';
// import VideoSnippetGenerator from './components/Social/VideoSnippetGenerator';
// import VideoContentSuggestions from './components/Social/VideoContentSuggestions';
// import VideoGenerator from './components/Social/VideoGenerator';
// import AnalysisForm from './components/ContentExpansion/AnalysisForm';
// import ResultsDisplay from './components/ContentExpansion/ResultsDisplay';
// import BacklinkAnalysis from './components/backlinkAnalysis/AnalysisForm';
// import AnalysisHistory from './components/backlinkAnalysis/AnalysisHistory';
// import BacklinkProgram from './components/BacklinkProgram/BacklinkProgram';

// // Content & Social Boost (legacy)
// import ContentCalendar from './components/ContentSocialBoost/ContentCalendar';
// import HashtagGenerator from './components/ContentSocialBoost/HashtagGenerator';
// import ImageSuggestions from './components/ContentSocialBoost/ImageSuggestions';
// import VoiceToBlog from './components/ContentSocialBoost/VoiceToBlog';

// // Smart Sales Enablers (legacy)
// import SEOChatbotWidget from './components/SEOChatbot/SEOChatbotWidget';
// import AdCampaignGenerator from './components/SEOChatbot/AdCampaignGenerator';
// import AdCampaignManager from './components/SEOChatbot/AdCampaignManager';
// import SocialMediaManager from './components/SEOChatbot/SocialMediaManager';

// // Analytics & Conversion (legacy)
// import PerformanceDashboard from './components/AnalyticsAndConversion/PerformanceDashboard';
// import LandingPageBuilder from './components/AnalyticsAndConversion/LandingPageBuilder';

// // SEO Plan Generator & Conversion (legacy)
// import SEOPlanDashboard from './components/Generator/SeoPlannerDashboard';

// // Google (legacy)
// import GoogleAnalyticsConnect from './components/google/GoogleAnalyticsConnect';
// import OAuthClientManager from './components/google/OAuthClientManager';

// // Pages (legacy)
// import Dashboard from './pages/Dashboard';
// import Login from './pages/Login';
// import Register from './pages/Register';
// import Profile from './pages/Profile';
// import Projects from './pages/Projects';
// import ProjectDashboard from './pages/ProjectDashboard';
// import AdminProjectMapping from './pages/AdminProjectMapping';

// const theme = createTheme({ /* ... unchanged ... */ });

// function LandingPageWrapper() {
//   // ... unchanged ...
// }

// function App() {
//   return (
//     <ThemeProvider theme={theme}>
//       <CssBaseline />
//       <AppProvider>
//         <Router>
//           <NotificationContainer />
//           <Routes>
//             {/* Public routes */}
//             <Route path="/" element={<AuditFormNew />} />
//             <Route path="/loading" element={<LoadingScreen />} />
//             <Route path="/login" element={<Login />} />
//             <Route path="/register" element={<Register />} />
//             <Route path="/about" element={<AboutUs />} />
//             {/* Optional: keep landing page sections if needed */}
//             <Route path="/home" element={<LandingPageWrapper />} />
//             <Route path="/features" element={<LandingPageWrapper />} />
//             <Route path="/pricing" element={<LandingPageWrapper />} />
//             <Route path="/contact" element={<LandingPageWrapper />} />

//             {/* New Dashboard */}

//             <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
//               {/* Brand audit pages */}
//               <Route path="brand-audit" element={<BrandAuditWizard />} />
//               <Route path="brand/domain" element={<DomainAuthorityPage />} />
//               <Route path="brand/speed" element={<PageSpeedPage />} />
//               <Route path="brand/trends" element={<GoogleTrendsPage />} />
//               <Route path="brand/social" element={<SocialMediaPage />} />
//               <Route path="brand/gmb" element={<GmbAuditPage />} />
//               <Route path="brand/techseo" element={<TechnicalSEOPage />} />
//               <Route path="brand/indexing" element={<IndexingPage />} />
//               <Route path="brand/presence" element={<BrandPresencePage />} />

//               {/* Competitor audit pages */}
//               <Route path="competitor/domain" element={<CompetitorDomainPage />} />
//               <Route path="competitor/speed" element={<CompetitorSpeedPage />} />
//               <Route path="competitor/trends" element={<CompetitorTrendsPage />} />
//               <Route path="competitor/techseo" element={<CompetitorTechSEOPage />} />
//               <Route path="competitor/ads" element={<PaidAdsPage />} />
//               <Route path="competitor/ai" element={<AIVisibilityPage />} />

//               {/* Research pages */}
//               <Route path="keywords" element={<KeywordResearchPage />} />
//                <Route path="paa" element={<PeopleAlsoAsk />} /> 
//               <Route path="reco" element={<AIRecommendations />} />

//               {/* Default redirect */}
//               <Route index element={<Navigate to="brand/domain" replace />} />
//             </Route>

//             {/* Legacy routes – keep for backward compatibility under /legacy or as fallback */}
//             <Route path="/legacy/*" element={
//               <ProtectedRoute>
//                 <Layout>
//                   <Routes>
//                     <Route path="/" element={<Dashboard />} />
//                     <Route path="seo/Complete-Audit" element={<CompleteAuditDashboard />} />
//                     <Route path="seo/Audit" element={<AuditDashboard />} />
//                     <Route path="seo/analyzer" element={<SEOAnalyzer />} />
//                     <Route path="seo/keywords" element={<KeywordResearch />} />
//                     <Route path="seo/meta-tags" element={<MetaTagGenerator />} />
//                     <Route path="seo/reports" element={<Reports />} />
//                     <Route path="social/content" element={<ContentSuggestions />} />
//                     <Route path="social/content-generator" element={<ContentGenerator />} />
//                     <Route path="social/publisher" element={<SocialPublisher />} />
//                     <Route path="social/analytics" element={<SocialAnalytics />} />
//                     <Route path="social/AdCopy" element={<AdCopyGenerator />} />
//                     <Route path="social/VideoContent" element={<CompleteVideoContent />} />
//                     <Route path="social/VideoSnippet" element={<VideoSnippetGenerator />} />
//                     <Route path="social/VideoContentSuggestions" element={<VideoContentSuggestions />} />
//                     <Route path="social/VideoGenerator" element={<VideoGenerator />} />
//                     <Route path="ContentExpansion/Analysis" element={<AnalysisForm />} />
//                     <Route path="ContentExpansion/ResultDisplay" element={<ResultsDisplay />} />
//                     <Route path="backlinkAnalysis/Backlink" element={<BacklinkAnalysis />} />
//                     <Route path="backlinkAnalysis/BackLinkHistory" element={<AnalysisHistory />} />
//                     <Route path="BacklinkProgram/BacklinkProgram" element={<BacklinkProgram />} />
//                     <Route path="ContentSocialBoost/ContentCalender" element={<ContentCalendar />} />
//                     <Route path="ContentSocialBoost/HashtagGenerator" element={<HashtagGenerator />} />
//                     <Route path="ContentSocialBoost/ImageSuggestions" element={<ImageSuggestions />} />
//                     <Route path="ContentSocialBoost/VoiceToBlog" element={<VoiceToBlog />} />
//                     <Route path="SEOChatbot/SEOChatbotWidget" element={<SEOChatbotWidget />} />
//                     <Route path="SEOChatbot/AdCampaignGenerator" element={<AdCampaignGenerator />} />
//                     <Route path="SEOChatbot/AdCampaignManager" element={<AdCampaignManager />} />
//                     <Route path="SEOChatbot/SocialMediaManager" element={<SocialMediaManager />} />
//                     <Route path="AnalyticsAndConversion/PerformanceDashboard" element={<PerformanceDashboard />} />
//                     <Route path="AnalyticsAndConversion/LandingPageBuilder" element={<LandingPageBuilder />} />
//                     <Route path="Generator/Dashboard" element={<SEOPlanDashboard />} />
//                     <Route path="google/GoogleAnalyticsConnect" element={<GoogleAnalyticsConnect />} />
//                     <Route path="google/OAuthClientManager" element={<OAuthClientManager />} />
//                     <Route path="profile" element={<Profile />} />
//                     <Route path="projects" element={<Projects />} />
//                     <Route path="project-dashboard" element={<ProjectDashboard />} />
//                     <Route path="project-Mapping" element={<AdminProjectMapping />} />
//                     <Route path="*" element={<Navigate to="/legacy" replace />} />
//                   </Routes>
//                 </Layout>
//               </ProtectedRoute>
//             } />

//             {/* Fallback: redirect all unmatched to home */}
//             <Route path="*" element={<Navigate to="/" replace />} />
//           </Routes>
//         </Router>
//       </AppProvider>
//     </ThemeProvider>
//   );
// }

// export default App;
